import { Button, CircularProgress, Checkbox, FormControlLabel } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import InputField from "components/Input/InputField";
import { API_URL } from "const/constants";
import { finishAuthenticatedLogin } from "utils/postLoginNavigation";
import useKothar from "context/useKothar";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Visibility,
  VisibilityOff,
  People,
  School,
  AccountBalance,
  Assignment,
  Receipt,
  TrendingUp,
  ArrowForward,
  ArrowBack,
} from "@mui/icons-material";

const features = [
  {
    icon: <People sx={{ fontSize: 80 }} />,
    title: "Client Management",
    description:
      "Comprehensive client lifecycle management with tracking, archiving, and detailed client profiles.",
    color: "from-blue-500 to-blue-600",
    image:
      "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",
  },
  {
    icon: <School sx={{ fontSize: 80 }} />,
    title: "Student & Admission",
    description:
      "Manage student admissions, track applications, and handle academic information seamlessly.",
    color: "from-purple-500 to-purple-600",
    image:
      "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",
  },
  {
    icon: <AccountBalance sx={{ fontSize: 80 }} />,
    title: "Financial Management",
    description:
      "Track accounts, invoices, profit/loss analysis, and financial reporting for your business.",
    color: "from-green-500 to-green-600",
    image:
      "https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",
  },
  {
    icon: <Assignment sx={{ fontSize: 80 }} />,
    title: "Task & Project Management",
    description:
      "Assign tasks, track progress, manage deadlines, and stay organized with our task system.",
    color: "from-orange-500 to-orange-600",
    image:
      "https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",
  },
  {
    icon: <Receipt sx={{ fontSize: 80 }} />,
    title: "Invoice Management",
    description:
      "Create, manage, and track invoices with dynamic company details and payment tracking.",
    color: "from-pink-500 to-pink-600",
    image:
      "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",
  },
  {
    icon: <TrendingUp sx={{ fontSize: 80 }} />,
    title: "Analytics & Reports",
    description:
      "Get insights with real-time statistics, charts, and comprehensive reporting dashboard.",
    color: "from-indigo-500 to-indigo-600",
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",
  },
];

export default function Login() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [data, setData] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleRememberMe = (e) => {
    setRememberMe(e.target.checked);
  };

  const [{ token }, { setToken }] = useKothar();

  const validateEmail = (email) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const payload = validateEmail(data?.username)
      ? { password: data?.password, email: data?.username?.trim() }
      : { ...data, username: data?.username?.trim() };

    mutate(payload);
  };

  async function postData(payload) {
    delete axios.defaults.headers.common["Authorization"];

    const resp = await axios.post(`${API_URL}/auth/authenticate`, payload);

    return resp;
  }

  const {
    data: loggedData,
    mutate,
    isLoading,
  } = useMutation(postData, {
    onSuccess: async (res) => {
      // Clear React Query cache to ensure fresh data for new user
      queryClient.clear();

      setData({
        username: "",
        password: "",
      });

      // Store only username if remember me is checked
      if (rememberMe) {
        localStorage.setItem("rememberedUsername", data?.username);
      } else {
        localStorage.removeItem("rememberedUsername");
      }

      const responseData = res?.data;

      // First-login: email not yet verified → collect OTP
      if (responseData?.requiresEmailVerification) {
        sessionStorage.setItem("pendingVerificationEmail", responseData?.email || data?.username);
        toast.info("Please verify your email with the OTP we just sent.");
        navigate("/email/verify-otp");
        return;
      }

      // MFA flow
      if (responseData?.isMfa) {
        localStorage.setItem("userEmail", data?.username);
        toast.success("OTP sent to your email.");
        navigate("/email/verify");
        return;
      }

      // Normal login
      finishAuthenticatedLogin(responseData, {
        navigate,
        setToken,
        email: data?.username,
        queryClient,
        successMessage: responseData?.message || "Logged in successfully",
      });
    },
    onError(err) {
      const message =
        err?.response?.data?.errorMessage?.replace(/^EXCEPTION : /, "") ??
        "Invalid email or password";
      toast.error(message);
    },
  });

  // Check for remembered username on component mount
  useEffect(() => {
    const rememberedUsername = localStorage.getItem("rememberedUsername");

    if (rememberedUsername) {
      setData((prev) => ({
        ...prev,
        username: rememberedUsername,
      }));
      setRememberMe(true);
    }
  }, []);

  // Auto-advance carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % features.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + features.length) % features.length);
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % features.length);
  };

  const handleSlideClick = (index) => {
    setCurrentSlide(index);
  };

  return (
    <div className="min-h-screen flex bg-app-surface">
      {/* Left Side - Feature Slider */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-800 to-slate-900 relative overflow-hidden">
        <div className="relative w-full h-full flex items-center justify-center p-8">
          {/* Slides Container */}
          <div className="relative w-3/4 max-w-4xl h-3/4">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                  index === currentSlide ? "opacity-100 z-10" : "opacity-0 z-0"
                }`}
              >
                {/* Full Screen Background Image */}
                <div
                  className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                  style={{
                    backgroundImage: `url(${feature.image})`,
                  }}
                >
                  {/* Subtle Overlay for Text Readability */}
                  <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-black/30 to-black/40" />
                </div>

                {/* Content Overlay */}
                <div className="relative z-10 flex items-center justify-center h-full">
                  <div className="text-center px-8 w-full">
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 drop-shadow-2xl">
                      {feature.title}
                    </h2>
                    <p className="text-xl md:text-2xl text-slate-100 max-w-3xl mx-auto drop-shadow-lg leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            {/* Navigation Arrows */}
            <button
              onClick={handlePrevSlide}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-all duration-200 backdrop-blur-sm z-20"
              aria-label="Previous slide"
            >
              <ArrowBack />
            </button>
            <button
              onClick={handleNextSlide}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-all duration-200 backdrop-blur-sm z-20"
              aria-label="Next slide"
            >
              <ArrowForward />
            </button>

            {/* Slide Indicators */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-20">
              {features.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleSlideClick(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentSlide
                      ? "w-8 bg-app-surface"
                      : "w-2 bg-white/40 hover:bg-white/60"
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6 md:p-8 lg:p-12">
        <div className="w-full max-w-md">
          {/* Glassmorphism Card */}
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 overflow-hidden">
            {/* Header Section */}
            <div className="px-8 pt-10 pb-6">
              <div className="text-center mb-8">
                <h2 className="text-4xl font-bold text-white mb-3 drop-shadow-lg">Welcome Back</h2>
                <p className="text-slate-300 text-base">Sign in to access your dashboard</p>
              </div>
            </div>

            {/* Form Section */}
            <div className="px-8 pb-10">
              <form className="space-y-5" onSubmit={handleSubmit}>
                <div className="relative w-full">
                  <InputField
                    label="Username/Email"
                    placeholder="Enter Username or E-mail"
                    name="username"
                    required
                    type="text"
                    value={data?.username}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="relative w-full">
                  <InputField
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    name="password"
                    label="Password"
                    required
                    value={data?.password}
                    onChange={handleInputChange}
                    endIcon={
                      showPassword ? (
                        <VisibilityOff onClick={handleClickShowPassword} />
                      ) : (
                        <Visibility onClick={handleClickShowPassword} />
                      )
                    }
                  />
                </div>

                <div className="flex items-center justify-between pt-1">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={rememberMe}
                        onChange={handleRememberMe}
                        color="primary"
                        size="small"
                        sx={{
                          color: "rgba(255, 255, 255, 0.7)",
                          "&.Mui-checked": {
                            color: "#3b82f6",
                          },
                        }}
                      />
                    }
                    label={<span className="text-sm text-slate-300">Remember me</span>}
                  />
                  <Link
                    to="/forgot"
                    className="text-sm text-blue-400 hover:text-blue-300 font-medium transition-colors duration-200 underline-offset-2 hover:underline"
                  >
                    Forgot Password?
                  </Link>
                </div>

                <div className="pt-4">
                  <Button
                    variant="contained"
                    fullWidth
                    type="submit"
                    size="large"
                    sx={{
                      py: 1.75,
                      fontSize: "1rem",
                      fontWeight: 600,
                      textTransform: "none",
                      borderRadius: "12px",
                      background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                      boxShadow: "0 4px 14px 0 rgba(59, 130, 246, 0.5)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                        boxShadow: "0 6px 20px 0 rgba(59, 130, 246, 0.6)",
                        transform: "translateY(-1px)",
                      },
                      transition: "all 0.2s ease-in-out",
                    }}
                    startIcon={isLoading && <CircularProgress size={20} color="inherit" />}
                    disabled={isLoading}
                  >
                    {isLoading ? "Signing in..." : "Sign In"}
                  </Button>
                </div>
              </form>
            </div>
          </div>

          {/* Mobile Indicators (shown only on mobile) */}
          <div className="lg:hidden mt-8 flex justify-center gap-2">
            {features.map((_, index) => (
              <button
                key={index}
                onClick={() => handleSlideClick(index)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? "w-8 bg-blue-500"
                    : "w-1.5 bg-app-surface-muted0 hover:bg-slate-400"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
